import useNotificationHandler from "hooks/useNotificationHandler";
import { MUTATION_KEYS, QUERY_KEYS } from "library/shared";
import { IEmployee, IEmployeeView } from "library/types";
import { useMutation, useQueryClient } from "react-query";
import api from "service/api";
import { useInvalidateCompanyEmployeeViews } from "./useGetCompanyEmployeeViews";
import { useInvalidateNeedsAttentionData } from "./useGetNeedsAttentionData";
import { useInvalidateCompanyPackages } from "./useGetCompanyBenefitPackages";

type Params = {
  countryCode: number;
  clientInternalNumberGos: number;
  benefitImplementationTag: number;
  benefitPackageTag: number;
  employeeEnrollmentTag: number;
  employeeEmail: string;
  approve: boolean;
  payload: {
    rowVersion: string;
  };
};

const updateEmployeeEnrollmentStatus = async ({
  countryCode,
  clientInternalNumberGos,
  employeeEmail,
  employeeEnrollmentTag,
  benefitImplementationTag,
  benefitPackageTag,
  approve,
  payload,
}: Params) => {
  const response = await api.patch(
    `/employeeenrollment/approveorreject/${countryCode}/${clientInternalNumberGos}/${benefitImplementationTag}/${benefitPackageTag}/${employeeEmail}/${employeeEnrollmentTag}/${approve}`,
    payload
  );

  return response.data as IEmployee;
};

export function useUpdateEmployeeEnrollmentStatus(
  refetch = true,
  onlyActiveImplementations = true
) {
  const queryClient = useQueryClient();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  const invalidateNeedsAttentionData = useInvalidateNeedsAttentionData();

  return useMutation(updateEmployeeEnrollmentStatus, {
    mutationKey: MUTATION_KEYS.updateEmployeeEnrollmentStatus,
    onSuccess: (employeeEnrollmentList: any) => {
      const countryCode = employeeEnrollmentList[0]?.countryCode;
      const clientInternalNumberGos =
        employeeEnrollmentList[0]?.clientInternalNumberGos;
      invalidateNeedsAttentionData();

      if (refetch) {
        queryClient.resetQueries({
          queryKey: QUERY_KEYS.companyAction,
        });
        queryClient.resetQueries({
          queryKey: QUERY_KEYS.companyPackages,
        });
        queryClient.resetQueries({
          queryKey: QUERY_KEYS.companyEmployeeViews,
        });
      } else {
        const previousEmployeeData = queryClient.getQueryData<IEmployeeView[]>([
          QUERY_KEYS.companyEmployeeViews,
          countryCode?.toString(),
          clientInternalNumberGos?.toString(),
          onlyActiveImplementations,
        ]);
        const updatedEmployeeData = previousEmployeeData?.map(
          (employee: IEmployeeView) => {
            const employeeEnrollment = employeeEnrollmentList[0];
            if (
              employee.employeeLoginEmailAddress ===
                employeeEnrollment?.employeeLoginEmailAddress &&
              !employee.isDependent
            ) {
              return {
                ...employee,
                expanded: true,
                selected: true,
                enrollments: [
                  ...(employee.enrollments || []).map((enrollment) => {
                    const updatedEnrollment = employeeEnrollmentList.find(
                      (item) =>
                        enrollment?.employeeEnrollmentTag ===
                        item.employeeEnrollmentTag
                    );
                    if (updatedEnrollment) {
                      return updatedEnrollment;
                    } else {
                      return enrollment;
                    }
                  }),
                ],
              };
            }
            return employee;
          }
        );
        queryClient.setQueryData(
          [
            QUERY_KEYS.companyEmployeeViews,
            countryCode.toString(),
            clientInternalNumberGos.toString(),
            onlyActiveImplementations,
          ],
          updatedEmployeeData
        );
      }
    },
    onError: (error) => {
      // handle error
      handleAddAxiosErrorNotification(error);
    },
  });
}
